import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';

/**
 * Service for managing the Android/iOS app icon badge count.
 * Wraps the Capacitor Badge plugin with platform safety checks.
 */
export const badgeService = {
  /**
   * Check if badge API is available on current platform
   */
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  },

  /**
   * Set the app icon badge to a specific count
   */
  async setBadgeCount(count: number): Promise<void> {
    if (!this.isSupported()) return;
    
    try {
      if (count <= 0) {
        await Badge.clear();
        console.log('[Badge] Badge cleared (count was 0 or negative)');
      } else {
        await Badge.set({ count });
        console.log('[Badge] Set badge count:', count);
      }
    } catch (error) {
      // Some Android launchers don't support badges - fail silently
      console.warn('[Badge] Error setting badge:', error);
    }
  },

  /**
   * Clear the app icon badge completely
   */
  async clearBadge(): Promise<void> {
    if (!this.isSupported()) return;
    
    try {
      await Badge.clear();
      console.log('[Badge] Badge cleared');
    } catch (error) {
      console.warn('[Badge] Error clearing badge:', error);
    }
  },

  /**
   * Check if the current device/launcher supports badges
   */
  async checkSupport(): Promise<boolean> {
    if (!this.isSupported()) return false;
    
    try {
      const { isSupported } = await Badge.isSupported();
      return isSupported;
    } catch (error) {
      console.warn('[Badge] Error checking support:', error);
      return false;
    }
  }
};
